import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { EmptyState, Page, StatusBadge } from "../../../components/ui";
import {
  AnalyticsPanel,
  DetailHeader,
  DetailSkeleton,
  DetailTabs,
  FactList,
  MediaPlayer,
  OnOffBadge,
  TagList,
  useMediaAnalytics,
  useStoredDetail,
} from "../../../components/detail";
import CommentModeration from "../comments/CommentModeration";
import { getEpisode } from "../../../services/shows";
import { formatDuration, getDate } from "../../../utils/format";
import { stripHtml } from "../../../utils/list";
import { canViewAnalytics } from "../../../utils/roles";

/**
 * Episode details page shared by TV show and podcast episodes.
 *
 * - `idParam`: name of the route param holding the episode id
 * - `storeKey`: uploadMediaReducer field holding the episode
 * - `setEpisodeInfo`: redux action creator storing the episode
 * - `pagePath(id)`: path of this page; leaving it clears the stored episode
 * - `commentsType`: kind of episode (show / podcast), comments are moderated by episode id
 * - `noMediaText`: translation key shown when no media was uploaded
 */
const EpisodeInfoPage = ({
  idParam,
  storeKey,
  setEpisodeInfo,
  pagePath,
  commentsType,
  noMediaText,
}) => {
  const { t } = useTranslation();
  const episodeId = useParams()[idParam];
  const allowedAdmins = canViewAnalytics();

  const { item: episode, loading } = useStoredDetail({
    load: () => getEpisode(episodeId),
    setInfo: setEpisodeInfo,
    storeKey,
    pagePath: pagePath(episodeId),
  });


  const analytics = useMediaAnalytics(
    [episode?.media_id],
    episode?.createdAt,
    allowedAdmins
  );

  const [tab, setTab] = useState("review");

  if (loading) {
    return (
      <Page>
        <DetailSkeleton />
      </Page>
    );
  }

  if (!episode) {
    return (
      <Page>
        <EmptyState icon="ri-film-line">
          {t("No data found for the provided ID!")}
        </EmptyState>
      </Page>
    );
  }

  const tabs = [
    ...(allowedAdmins
      ? [{ id: "description", label: t("Analytics"), icon: "ri-bar-chart-2-line" }]
      : []),
    { id: "review", label: t("Comments"), icon: "ri-chat-3-line" },
  ];

  return (
    <Page>
      <DetailHeader
        title={episode.title}
        badges={<StatusBadge active={Boolean(episode.status)} />}
      />

      <div className="detail-layout">
        <div className="detail-stack">
          <MediaPlayer
            mediaId={episode.media_id}
            emptyText={t("Episode has not been uploaded yet")}
          />
          <section className="ui-card detail-pad">
            <div className="detail-section">
              <h3 className="detail-section-title">{t("desc")}</h3>
              <p className="detail-description">
                {stripHtml(episode.description) || "—"}
              </p>
            </div>
            <TagList
              label={t("tags")}
              items={(episode.jw_tags || []).filter(
                (tag) => !tag.endsWith("-episode")
              )}
            />
            <TagList label={t("Seo Keywords")} items={episode.seo_tags} />
          </section>
        </div>

        <section className="ui-card detail-pad">
          {episode.thumbnail?.static_thumbnail_url && (
            <img
              className="detail-poster"
              src={episode.thumbnail.static_thumbnail_url}
              alt=""
            />
          )}
          <FactList
            items={[
              { label: t("availability"), value: episode.availability },
              {
                label: t("Duration"),
                value: formatDuration(episode.duration),
              },
              {
                label: t("Release Date"),
                value: getDate(episode.release_year),
              },
              {
                label: t("Default Language"),
                value: episode.default_language,
              },
              {
                label: t("Monetization"),
                value: <OnOffBadge on={Boolean(episode.monetization)} />,
              },
            ]}
          />
        </section>
      </div>

      <section className="ui-card">
        <DetailTabs tabs={tabs} value={tab} onChange={setTab} />
        <div className="detail-tab-panel">
          {allowedAdmins && tab === "description" && (
            <AnalyticsPanel {...analytics} emptyText={t(noMediaText)} />
          )}
          {tab === "review" && <CommentModeration contentType="episode" contentId={episode?._id} />}
        </div>
      </section>
    </Page>
  );
};

export default EpisodeInfoPage;
